import {
  Box,
  Container,
  Stack,
  Typography,
  Tabs,
  Tab,
  styled,
  alpha,
} from "@mui/material";
import Template from "@/templates/Templates";
import { BackButton, DeleteButton, BackDropLoading } from "@/organism";
import useDetail from "./useDetail";
import { useState } from "react";
import {
  Article as ArticleIcon,
  Architecture as ArchitectureIcon,
} from "@mui/icons-material";

// 스타일링된 탭 컴포넌트
const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 64,
  fontSize: "0.9rem",
  fontWeight: 500,
  textTransform: "none",
  transition: "0.2s",
  "&.Mui-selected": {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&.MuiTab-root": {
    padding: "12px 24px",
  },
  "& .MuiTab-iconWrapper": {
    marginRight: 8,
    marginBottom: "0 !important",
  },
}));

// 탭 컨테이너
const TabContainer = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  boxShadow: `0 1px 3px 0 ${alpha(theme.palette.common.black, 0.04)}`,
  position: "relative",
  zIndex: 1,
  marginBottom: theme.spacing(3),
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "1px",
    backgroundColor: theme.palette.divider,
  },
}));

export const Detail = () => {
  const {
    isLoading,
    templateInfo,
    handleTemplateDelete,
    handleTemplateUpdate,
  } = useDetail();

  const [activeTab, setActiveTab] = useState<number>(0);

  // 탭 변경 핸들러
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth={"xl"}>
      <BackDropLoading isLoading={isLoading} />
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        gap={2}
        alignItems={"end"}
        mb={3}
      >
        <Stack direction={"column"} gap={1}>
          <BackButton to={"/template"}>템플릿 목록</BackButton>
          <Typography
            component={"h1"}
            variant="h5"
            fontWeight={"bold"}
            gutterBottom
          >
            템플릿 상세 정보
          </Typography>

          {templateInfo && (
            <>
              <Typography variant="h6">{templateInfo.templateNm}</Typography>
            </>
          )}
        </Stack>
        {templateInfo && (
          <Stack direction={"row"} gap={1}>
            <DeleteButton
              onClick={() => handleTemplateDelete(templateInfo.templateId)}
            >
              삭제
            </DeleteButton>
          </Stack>
        )}
      </Box>

      {/* 탭 메뉴 */}
      <TabContainer>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <StyledTab
            label="기본 정보"
            icon={<ArticleIcon />}
            iconPosition="start"
          />
          <StyledTab
            label="템플릿 구조"
            icon={<ArchitectureIcon />}
            iconPosition="start"
          />
        </Tabs>
      </TabContainer>

      {/* 탭 컨텐츠 */}
      {templateInfo && (
        <Box
          sx={{
            mt: 2,
            transition: "all 0.3s ease",
            animation: activeTab === 1 ? "fadeIn 0.3s ease" : "none",
            "@keyframes fadeIn": {
              "0%": { opacity: 0.7, transform: "translateY(10px)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* 기본 정보 탭 */}
          {activeTab === 0 && (
            <Template.EditForm
              templateInfo={templateInfo}
              onSubmit={handleTemplateUpdate}
            />
          )}

          {/* 템플릿 구조 탭 */}
          {activeTab === 1 && <Template.CategoryForm />}
        </Box>
      )}
    </Container>
  );
};
